import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Table, ConfigProvider } from 'antd';
import { Owner } from '../../../types/Owner';
import Tag from '../../../utils/shared/components/Tags/Tag';
import './style/OwnersTable.css';

interface OwnersTableProps {
    owners: Owner[];
    selectedRowKeys: React.Key[];
    onSelectChange: (selectedRowKeys: React.Key[]) => void;
    onRowClick: (owner: Owner) => void;
}

const OwnersTable: React.FC<OwnersTableProps> = ({ owners, selectedRowKeys, onSelectChange, onRowClick }) => {
    const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
    const [sortField, setSortField] = useState<'name' | 'type' | null>(null);
    const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>(null);

    useEffect(() => {
        if (selectedRowKeys.length > 0) {
            const lastKey = selectedRowKeys[selectedRowKeys.length - 1];
            const ref = rowRefs.current[lastKey as string];
            if (ref) {
                ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [selectedRowKeys]);

    const sortedOwners = useMemo(() => {
        if (!sortField || !sortOrder) return owners;

        return [...owners].sort((a, b) => {
            let valueA: string;
            let valueB: string;

            if (sortField === 'type') {
                valueA = a.car ? 'машина' : 'сотрудник';
                valueB = b.car ? 'машина' : 'сотрудник';
            } else {
                valueA = a.name.toLowerCase();
                valueB = b.name.toLowerCase();
            }

            if (sortOrder === 'ascend') {
                return valueA.localeCompare(valueB);
            } else {
                return valueB.localeCompare(valueA);
            }
        });
    }, [owners, sortField, sortOrder]);

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        if (sorter.columnKey === 'name' || sorter.columnKey === 'type') {
            if (sorter.order === 'ascend') {
                setSortField(sorter.columnKey);
                setSortOrder('ascend');
            } else if (sorter.order === 'descend') {
                setSortField(sorter.columnKey);
                setSortOrder('descend');
            } else {
                setSortField(null);
                setSortOrder(null);
            }
        }
    };

    const columns = [
        {
            title: 'ФИО сотрудника / Гос. номер машины',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            sortOrder: sortField === 'name' ? sortOrder : null,
            width: 920,
            render: (text: string, record: Owner) => {
                const initialsClass = record.car ? 'user-initials-car' : 'user-initials-users';
                return (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className={initialsClass}>{text.slice(0, 2).toUpperCase()}</span>
                        {text}
                    </span>
                );
            },
        },
        {
            title: 'Тип',
            key: 'type',
            sorter: true,
            sortOrder: sortField === 'type' ? sortOrder : null,
            width: 920,
            render: (_: any, record: Owner) => (
                <Tag state={record.car ? 'error' : 'success'} border={true}>
                    {record.car ? 'Машина' : 'Сотрудник'}
                </Tag>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    return (
        <ConfigProvider theme={{ components: { Table: { headerBorderRadius: 0 } } }}>
            <Table
                className="table owners-table-sort"
                columns={columns}
                dataSource={sortedOwners}
                pagination={false}
                rowKey="UID"
                rowSelection={rowSelection}
                onChange={handleTableChange}
                showSorterTooltip={false}
                onRow={(record) => ({
                    ref: (node: HTMLTableRowElement) => {
                        rowRefs.current[record.UID] = node;
                    },
                    onClick: () => onRowClick(record),
                })}
            />
        </ConfigProvider>
    );
};

export default OwnersTable; 